import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { TrashIcon, EditIcon, CheckCircleIcon, XCircleIcon } from '../../../components/icons/Icons';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

export default function ExpenseCategoryManager({ appId }) {
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [editingCategory, setEditingCategory] = useState({ id: null, name: '' });

    const categoriesRef = collection(db, `/artifacts/${appId}/public/data/expenseCategories`);

    useEffect(() => {
        const q = query(categoriesRef, orderBy('name'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedCategories = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCategories(fetchedCategories);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [appId]);

    const handleAddCategory = async () => {
        if (!newCategoryName.trim()) return;
        try {
            await addDoc(categoriesRef, { name: newCategoryName.trim() });
            setNewCategoryName('');
        } catch (error) {
            console.error("Error adding category:", error);
            alert("Failed to add category.");
        }
    };

    const handleUpdateCategory = async () => {
        if (!editingCategory.id || !editingCategory.name.trim()) return;
        const categoryDocRef = doc(db, `/artifacts/${appId}/public/data/expenseCategories`, editingCategory.id);
        try {
            await updateDoc(categoryDocRef, { name: editingCategory.name.trim() });
            setEditingCategory({ id: null, name: '' }); // Exit editing mode
        } catch (error) {
            console.error("Error updating category:", error);
            alert("Failed to update category.");
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!window.confirm("Are you sure you want to delete this category?")) return;
        const categoryDocRef = doc(db, `/artifacts/${appId}/public/data/expenseCategories`, categoryId);
        try {
            await deleteDoc(categoryDocRef);
        } catch (error) {
            console.error("Error deleting category:", error);
            alert("Failed to delete category.");
        }
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <h3 className="text-xl font-semibold text-[#213368] mb-4">Manage Expense Categories</h3>
            <div className="flex space-x-2 mb-6">
                <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="New category name..."
                    className="flex-grow p-2 border border-gray-300 rounded-md shadow-sm"
                />
                <button
                    onClick={handleAddCategory}
                    className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700"
                >
                    Add
                </button>
            </div>

            <ul className="space-y-2">
                {categories.map(cat => (
                    <li key={cat.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                        {editingCategory.id === cat.id ? (
                            <input
                                type="text"
                                value={editingCategory.name}
                                onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                                className="flex-grow p-1 border border-blue-400 rounded-md"
                            />
                        ) : (
                            <span className="text-gray-800">{cat.name}</span>
                        )}

                        <div className="flex items-center space-x-3">
                            {editingCategory.id === cat.id ? (
                                <>
                                    <button onClick={handleUpdateCategory} className="text-green-500 hover:text-green-700"><CheckCircleIcon /></button>
                                    <button onClick={() => setEditingCategory({ id: null, name: '' })} className="text-red-500 hover:text-red-700"><XCircleIcon /></button>
                                </>
                            ) : (
                                <>
                                    <button onClick={() => setEditingCategory({ id: cat.id, name: cat.name })} className="text-gray-500 hover:text-blue-600"><EditIcon /></button>
                                    <button onClick={() => handleDeleteCategory(cat.id)}><TrashIcon /></button>
                                </>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}